import * as React from "react";

import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { ProvideInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";

import { Table2MultirowTestPageObject } from "./Table2MultirowTestPageObject";
import { MultirowRoot } from "./Table2Multirow.styles";

export const Table2MultirowInstanceCountContext = React.createContext<undefined | number>(undefined);

export function useTable2MultirowInstanceCount(): number {
    const result = React.useContext(Table2MultirowInstanceCountContext);
    if (result == undefined) {
        throw new Error("Element must be defined in multirow context");
    }
    return result;
}

export const Table2MultirowDisplayIndexContext = React.createContext<undefined | number>(undefined);

export function useTable2MultirowDisplayIndex(): number {
    const result = React.useContext(Table2MultirowDisplayIndexContext);
    if (result == undefined) {
        throw new Error("Element must be defined in multirow context");
    }
    return result;
}

export interface Table2MultirowProps {
    bindingPath: AbsoluteResolvedModelPath;
    instances: readonly string[];
    children: React.ReactNode[];
    onAddInstance: () => void;
    onRemoveInstance: (instance: string) => void;
    filteredUniqKey: string;
    showRowBorder?: boolean;
}

export function Table2Multirow(props: Table2MultirowProps): JSX.Element {
    useTestPageObject(
        () => Table2MultirowTestPageObject(props.instances, props.onAddInstance, props.onRemoveInstance),
        [props.instances]
    );

    return (
        <Table2MultirowInstanceCountContext.Provider value={props.instances.length}>
            {props.instances.map((instance, index) => (
                <MultirowRoot key={instance} showRowBorder={props.showRowBorder}>
                    <ProvideInstanceIndex key={`${instance}_${index}`} index={index} bindingPath={props.bindingPath}>
                        <Table2MultirowDisplayIndexContext.Provider value={index}>
                            <PushInstance
                                instance={instance}
                                bindingPath={props.bindingPath}
                                data-tid={"Table2Multirow"}>
                                <>{props.children}</>
                            </PushInstance>
                        </Table2MultirowDisplayIndexContext.Provider>
                    </ProvideInstanceIndex>
                </MultirowRoot>
            ))}
        </Table2MultirowInstanceCountContext.Provider>
    );
}
